import { useState } from 'react';
import { Box, Sheet, Stack, Typography } from '@mui/joy';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import { openAdClick } from '../../../api_ads_public';
import { AdCta, AdDisclosure, AdMark } from './AdParts';
import useAdImpression from './useAdImpression';


/** Нижний липкий баннер над навигацией. */
export function AdBannerBottom({ ad }) {
    const [closed, setClosed] = useState(false);
    const { ref, dwellMs } = useAdImpression(ad, { enabled: !closed });

    if (!ad || closed) return null;
    const click = () => openAdClick(ad, dwellMs());

    return (
        <Sheet
            ref={ref}
            variant="soft"
            onClick={click}
            sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1.2, py: 0.8, cursor: 'pointer',
                borderTop: '1px solid', borderColor: 'divider',
            }}
        >
            {ad.image_url && (
                <Box
                    sx={{
                        width: 44, height: 44, borderRadius: 'sm',
                        overflow: 'hidden', flexShrink: 0, bgcolor: 'neutral.softBg',
                    }}
                >
                    <img
                        src={ad.image_url}
                        alt=""
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                </Box>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography level="body-sm" noWrap sx={{ fontWeight: 600 }}>
                    {ad.title}
                </Typography>
                <AdDisclosure ad={ad} />
            </Box>

            <AdCta text={ad.cta_text} size="sm" onClick={click} />

            <Box
                component="button"
                aria-label="Скрыть рекламу"
                onClick={(e) => { e.stopPropagation(); setClosed(true); }}
                sx={{
                    border: 0, background: 'transparent', cursor: 'pointer',
                    color: 'neutral.400', display: 'flex', p: 0.3,
                }}
            >
                <CloseRoundedIcon sx={{ fontSize: 16 }} />
            </Box>
        </Sheet>
    );
}


/** Нативный промо-пост в ленте союзов. */
export function AdFeedNative({ ad }) {
    const { ref, dwellMs } = useAdImpression(ad);
    if (!ad) return null;
    const click = () => openAdClick(ad, dwellMs());

    const image = ad.image_wide_url || ad.image_url;

    return (
        <Sheet
            ref={ref}
            variant="outlined"
            onClick={click}
            sx={{ borderRadius: 'md', overflow: 'hidden', cursor: 'pointer', mb: 2 }}
        >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography level="body-sm" noWrap sx={{ fontWeight: 600 }}>
                        {ad.brand_name || 'Рекламодатель'}
                    </Typography>
                </Box>
                <AdMark sx={{ bgcolor: 'neutral.softBg', color: 'neutral.600' }} />
            </Stack>

            {image && (
                <Box sx={{ aspectRatio: '16 / 9', bgcolor: 'neutral.softBg' }}>
                    <img
                        src={image}
                        alt=""
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                </Box>
            )}

            <Box sx={{ p: 1.5 }}>
                <Typography level="title-sm">{ad.title}</Typography>
                {ad.text && (
                    <Typography level="body-sm" color="neutral" sx={{ mb: 1.2 }}>
                        {ad.text}
                    </Typography>
                )}
                <AdCta text={ad.cta_text} onClick={click} size="sm" />
            </Box>
        </Sheet>
    );
}


/** Строка в списке чатов. */
export function AdChatListItem({ ad }) {
    const { ref, dwellMs } = useAdImpression(ad);
    if (!ad) return null;
    const click = () => openAdClick(ad, dwellMs());

    return (
        <Box
            ref={ref}
            onClick={click}
            sx={{
                display: 'flex', gap: 1.5, alignItems: 'center',
                px: 2, py: 1.2, cursor: 'pointer',
                borderBottom: '1px solid', borderColor: 'divider',
                '&:hover': { bgcolor: 'neutral.softBg' },
            }}
        >
            <Box
                sx={{
                    width: 44, height: 44, borderRadius: '50%',
                    overflow: 'hidden', flexShrink: 0, bgcolor: 'neutral.softBg',
                }}
            >
                {ad.image_url && (
                    <img
                        src={ad.image_url}
                        alt=""
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={0.8} alignItems="center">
                    <Typography level="body-sm" noWrap sx={{ fontWeight: 600 }}>
                        {ad.title}
                    </Typography>
                    <AdMark sx={{ bgcolor: 'neutral.softBg', color: 'neutral.600' }} />
                </Stack>
                <Typography level="body-xs" color="neutral" noWrap>
                    {ad.text || ad.brand_name}
                </Typography>
            </Box>
        </Box>
    );
}


/** Горизонтальная карточка под чужой анкетой. */
export function AdProfileBottom({ ad }) {
    const { ref, dwellMs } = useAdImpression(ad);
    if (!ad) return null;
    const click = () => openAdClick(ad, dwellMs());

    return (
        <Sheet
            ref={ref}
            variant="outlined"
            onClick={click}
            sx={{ borderRadius: 'md', overflow: 'hidden', cursor: 'pointer', mt: 2 }}
        >
            {(ad.image_wide_url || ad.image_url) && (
                <Box sx={{ aspectRatio: '16 / 9', bgcolor: 'neutral.softBg' }}>
                    <img
                        src={ad.image_wide_url || ad.image_url}
                        alt=""
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                </Box>
            )}
            <Box sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography level="title-sm">{ad.title}</Typography>
                    <AdMark sx={{ bgcolor: 'neutral.softBg', color: 'neutral.600' }} />
                </Stack>
                {ad.text && (
                    <Typography level="body-sm" color="neutral" sx={{ mb: 1.2 }}>
                        {ad.text}
                    </Typography>
                )}
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <AdCta text={ad.cta_text} onClick={click} size="sm" />
                    <AdDisclosure ad={ad} />
                </Stack>
            </Box>
        </Sheet>
    );
}